import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { showSuccess } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { userService, type UserEmployeeLookup, type UserRoleLookup } from '@/services/userService'
import type { UserVm } from '@/api/types'

export const UserDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const params = useParams()
  const { user } = useAuthStore()

  const branchId = user?.selectedBranch?.branchId ?? 0
  const isEditMode = !!params.id

  const [employees, setEmployees] = useState<UserEmployeeLookup[]>([])
  const [roles, setRoles] = useState<UserRoleLookup[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [originalVm, setOriginalVm] = useState<UserVm | null>(null)

  const [employeeId, setEmployeeId] = useState<number | undefined>()
  const [roleId, setRoleId] = useState<number | undefined>()
  const [email, setEmail] = useState('')
  const [userName, setUserName] = useState('')
  const [password, setPassword] = useState('')
  const [isActive, setIsActive] = useState(true)

  const req = t('userPage.requiredMark')

  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        const [empList, roleList, users] = await Promise.all([
          userService.getEmployees(branchId),
          userService.getRoles(branchId),
          isEditMode ? Promise.resolve(null) : userService.getAllUsers(branchId, 1, 1000),
        ])

        let filteredEmployees = empList || []

        // For Add User, hide employees that already have a user (by empId)
        if (!isEditMode && users && Array.isArray(users.data)) {
          const usedEmpIds = new Set<number>((users.data || []).map((u: any) => u.empId))
          filteredEmployees = filteredEmployees.filter((e) => !usedEmpIds.has(e.id))
        }

        setEmployees(filteredEmployees || [])
        setRoles(roleList || [])
      } catch (error) {
        console.error('Failed to load user lookups', error)
      }
    }

    loadLookups()
  }, [branchId, isEditMode])

  useEffect(() => {
    if (!branchId || !isEditMode) return

    const id = Number(params.id)
    if (!id) return

    const loadUser = async () => {
      try {
        setLoading(true)
        const vm = await userService.getUserById(branchId, id)
        if (!vm) return
        setOriginalVm(vm)

        setEmployeeId(vm.employeeId ?? vm.empId ?? vm.EmpId)
        setRoleId(vm.roleId ?? vm.RoleId)
        setEmail((vm.email ?? vm.Email ?? '') as string)
        setUserName((vm.userName ?? vm.UserName ?? '') as string)
        setIsActive((vm.isActive ?? vm.IsActive ?? 1) === 1 || vm.isActive === true)
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('userPage.toastLoadFailed'))
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [branchId, isEditMode, params.id, t])

  const handleEmployeeChange = (value: string) => {
    const id = Number(value)
    setEmployeeId(id)
    const emp = employees.find((e) => e.id === id)
    if (emp) {
      if (!email) {
        setEmail(emp.email || '')
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!branchId) {
      toast.error(t('userPage.toastSelectBranch'))
      return
    }
    if (!employeeId) {
      toast.error(t('userPage.toastEmployeeRequired'))
      return
    }
    if (!roleId) {
      toast.error(t('userPage.toastRoleRequired'))
      return
    }
    if (!email) {
      toast.error(t('userPage.toastEmailRequired'))
      return
    }
    if (!userName) {
      toast.error(t('userPage.toastUserNameRequired'))
      return
    }
    if (!isEditMode && !password) {
      toast.error(t('userPage.toastPasswordRequired'))
      return
    }

    const baseVm: UserVm = originalVm ? { ...originalVm } : {}

    const existingPassword =
      (baseVm as any)?.userPassword ??
      (baseVm as any)?.UserPassword ??
      (baseVm as any)?.password ??
      (baseVm as any)?.Password ??
      ''

    const vm: UserVm = {
      id: isEditMode ? (baseVm.id ?? Number(params.id)) : 0,
      empId: employeeId,
      userName,
      roleId,
      userPassword: password || existingPassword,
      email,
      isActive,
      branchId,
    }

    try {
      setSaving(true)
      toast.dismiss()
      if (isEditMode) {
        await userService.updateUser(vm)
        await showSuccess(t('userPage.savedUpdatedTitle'), t('userPage.savedUpdatedMessage'))
      } else {
        await userService.createUser(vm)
        await showSuccess(t('userPage.savedCreatedTitle'), t('userPage.savedCreatedMessage'))
      }
      navigate('/security/users')
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('userPage.toastSaveFailed'))
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('userPage.pageTitle')} description={t('userPage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('userPage.branchRequiredTitle')}
              description={t('userPage.branchRequiredDescriptionForm')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={isEditMode ? t('userPage.pageTitleEdit') : t('userPage.pageTitleAdd')}
        description={isEditMode ? t('userPage.pageDescriptionEdit') : t('userPage.pageDescriptionAdd')}
      />

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>{req} {t('userPage.labelEmployee')}</Label>
                <Select
                  value={employeeId ? String(employeeId) : ''}
                  onValueChange={handleEmployeeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('userPage.placeholderSelectEmployee')} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={String(e.id)}>
                        {e.fullName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{req} {t('userPage.labelRole')}</Label>
                <Select
                  value={roleId ? String(roleId) : ''}
                  onValueChange={(v) => setRoleId(Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('userPage.placeholderSelectRole')} />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{req} {t('userPage.labelEmail')}</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label>{req} {t('userPage.labelUserName')}</Label>
                <Input value={userName} onChange={(e) => setUserName(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label>
                  {isEditMode ? (
                    <>
                      {t('userPage.labelPassword')} {t('userPage.labelPasswordEditHint')}
                    </>
                  ) : (
                    <>
                      {req} {t('userPage.labelPassword')}
                    </>
                  )}
                </Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('userPage.labelIsActive')}</Label>
                <Select
                  value={isActive ? '1' : '0'}
                  onValueChange={(v) => setIsActive(v === '1')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{t('userPage.yes')}</SelectItem>
                    <SelectItem value="0">{t('userPage.no')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={saving || loading}
                onClick={() => navigate('/security/users')}
              >
                {t('userPage.cancel')}
              </Button>
              <Button type="submit" disabled={saving || loading}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('userPage.saving') : t('userPage.save')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
